// Admin Service - Admin dashboard API calls

import api from './api';
import { buildQueryString } from '../utils/urlHelpers';
import type {
  HealthStatus,
  SyncJobStats,
  SyncStatus,
  SystemHealth,
  RagDocumentsResponse,
  RagDocumentDetail,
  RagStats,
  RagChunksResponse,
  SearchConfig,
  SearchResult,
  SearchConfigSnapshot,
  RAGTestCase,
  TestSuiteRun,
  DocumentSyncJobsResponse,
  DocumentSyncHealth,
  HtmlPagesResponse,
  HtmlPageDetail,
  HtmlPagesStats,
  HtmlChunksResponse,
  IngestionRecordsResponse,
  IngestionRecordDetail,
  IngestionStats,
  IngestionCategory,
  TripReportChunksResponse,
  TripReportChunksStats,
} from '../types/api';

export const adminService = {
  /**
   * Get basic health status
   */
  getHealth: async (): Promise<HealthStatus> => {
    const response = await api.get<HealthStatus>('/health');
    return response.data;
  },

  /**
   * Get comprehensive system health (admin only)
   */
  getSystemHealth: async (): Promise<SystemHealth> => {
    const response = await api.get<SystemHealth>('/admin/health');
    return response.data;
  },

  /**
   * Get sync stats overview
   */
  getSyncStats: async (opts?: { fresh?: boolean }): Promise<SyncStatus> => {
    // The server memoises this for 30 s (every dashboard tab polls it);
    // `fresh` bypasses the memo right after "Trigger sync" so the new job
    // row appears at once.
    const response = await api.get<SyncStatus>(
      '/admin/sync/stats',
      opts?.fresh ? { params: { fresh: true } } : undefined,
    );
    return response.data;
  },

  /**
   * Get recent sync jobs
   */
  getRecentSyncJobs: async (limit: number = 10): Promise<SyncJobStats[]> => {
    const response = await api.get<SyncJobStats[]>(`/admin/sync/jobs?limit=${limit}`);
    return response.data;
  },

  /**
   * Trigger a manual Sage sync (admin only)
   */
  triggerSync: async (
    syncType: 'full' | 'incremental' | 'customers' | 'products' | 'salespersons' | 'sales_history' | 'product_costs' = 'full'
  ): Promise<{ message: string; task_id: string }> => {
    const response = await api.post<{ message: string; task_id: string }>(
      `/admin/sync/trigger?sync_type=${syncType}`
    );
    return response.data;
  },

  /**
   * Trigger a manual Sales App sync (admin only)
   */
  triggerSalesSync: async (
    syncType: 'full' | 'sales_reps' | 'customer_locations' | 'quotes' | 'special_pricing' | 'sales_users' = 'full'
  ): Promise<{ message: string; task_id: string }> => {
    const response = await api.post<{ message: string; task_id: string }>(
      `/admin/sync/trigger-sales?sync_type=${syncType}`
    );
    return response.data;
  },

  /**
   * Get specific sync job details
   */
  getSyncJob: async (jobId: string): Promise<SyncJobStats> => {
    const response = await api.get<SyncJobStats>(`/admin/sync/jobs/${jobId}`);
    return response.data;
  },

  // ==========================================================================
  // RAG Pipeline Admin
  // ==========================================================================

  /**
   * Get RAG pipeline statistics
   */
  getRagStats: async (): Promise<RagStats> => {
    const response = await api.get<RagStats>('/admin/rag/stats');
    return response.data;
  },

  /**
   * List RAG documents with pagination and filters
   */
  getRagDocuments: async (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    document_type?: string;
    include_inactive?: boolean;
  }): Promise<RagDocumentsResponse> => {
    const query = buildQueryString({
      limit: params?.limit,
      offset: params?.offset,
      status: params?.status,
      document_type: params?.document_type,
      include_inactive: params?.include_inactive ? 'true' : undefined,
    });
    const response = await api.get<RagDocumentsResponse>(
      `/admin/rag/documents${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  /**
   * Get detailed document info with chunks
   */
  getRagDocument: async (documentId: string): Promise<RagDocumentDetail> => {
    const response = await api.get<RagDocumentDetail>(`/admin/rag/documents/${documentId}`);
    return response.data;
  },

  /**
   * Get URL for document PDF (for PDF viewer)
   */
  getRagDocumentPdfUrl: (documentId: string): string => {
    return `/api/admin/rag/documents/${documentId}/pdf`;
  },

  /**
   * Get document PDF as blob (with authentication headers)
   */
  getRagDocumentPdf: async (documentId: string): Promise<Blob> => {
    const response = await api.get(`/admin/rag/documents/${documentId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Process the next unprocessed document from FTP
   * By default queues for async processing. Use sync=true for synchronous (may timeout).
   */
  processNextRagDocument: async (sync: boolean = false): Promise<{
    status: string;
    task_id?: string;
    document_id?: string;
    filename?: string;
    ftp_path?: string;
    document_type?: string;
    page_count?: number;
    chunk_count?: number;
    text_length?: number;
    summary_stats?: {
      summarized: number;
      useless: number;
      errors: number;
    };
    remaining_candidates?: number;
    message?: string;
    error?: string;
  }> => {
    const response = await api.post(`/admin/rag/process-next${sync ? '?sync=true' : ''}`, {}, {
      timeout: sync ? 180000 : 30000, // 3 minutes for sync, 30s for async
    });
    return response.data;
  },

  /**
   * Get the status of a Celery task
   */
  getTaskStatus: async (taskId: string): Promise<{
    task_id: string;
    state: string;
    message?: string;
    result?: {
      status: string;
      document_id?: string;
      filename?: string;
      chunk_count?: number;
      error?: string;
    };
    error?: string;
    traceback?: string;
  }> => {
    const response = await api.get(`/admin/tasks/${taskId}`);
    return response.data;
  },

  /**
   * Retry a failed document
   */
  retryDocument: async (documentId: string): Promise<{
    message: string;
    task_id: string;
    document_id: string;
    triggered_by: string;
  }> => {
    const response = await api.post(`/admin/documents/${documentId}/retry`);
    return response.data;
  },

  /**
   * Remove a document (soft-delete) and its associated data
   */
  removeDocument: async (documentId: string): Promise<{
    message: string;
    document_id: string;
    removed_chunks: number;
    vertex_ai_removed: number;
    removed_by: string;
  }> => {
    const response = await api.delete(`/admin/documents/${documentId}`);
    return response.data;
  },

  /**
   * List RAG chunks with pagination and filters
   */
  getRagChunks: async (params?: {
    limit?: number;
    offset?: number;
    document_id?: string;
    is_useless?: boolean;
    has_summary?: boolean;
    search?: string;
  }): Promise<RagChunksResponse> => {
    const query = buildQueryString({
      limit: params?.limit,
      offset: params?.offset,
      document_id: params?.document_id,
      is_useless: params?.is_useless,
      has_summary: params?.has_summary,
      search: params?.search,
    });
    const response = await api.get<RagChunksResponse>(
      `/admin/rag/chunks${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  /**
   * Get LLM payload for a specific chunk - shows exactly what would be passed to the LLM
   */
  getChunkLLMPayload: async (chunkId: string): Promise<ChunkLLMPayload> => {
    const response = await api.get<ChunkLLMPayload>(`/admin/rag/chunks/${chunkId}/llm-payload`);
    return response.data;
  },

  /**
   * Get LLM payload for a search result - shows exactly what would be passed to the LLM
   * Uses the same ChunkLLMPayload formatting logic as the backend
   */
  getSearchResultLLMPayload: async (searchResult: SearchResultItem): Promise<SearchResultLLMPayload> => {
    const response = await api.post<SearchResultLLMPayload>('/admin/rag/search-result/llm-payload', searchResult);
    return response.data;
  },

  // ==========================================================================
  // Trip Report Chunks
  // ==========================================================================

  /**
   * List trip report chunks with pagination and filters
   */
  getTripReportChunks: async (params?: {
    limit?: number;
    offset?: number;
    chunk_type?: string;
    company_name?: string;
    has_raw_text?: boolean;
    is_useless?: boolean;
    search?: string;
  }): Promise<TripReportChunksResponse> => {
    const query = buildQueryString({
      limit: params?.limit,
      offset: params?.offset,
      chunk_type: params?.chunk_type,
      company_name: params?.company_name,
      has_raw_text: params?.has_raw_text,
      is_useless: params?.is_useless,
      search: params?.search,
    });
    const response = await api.get<TripReportChunksResponse>(
      `/admin/rag/trip-report-chunks${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  /**
   * Get trip report chunk statistics including raw_text coverage
   */
  getTripReportChunksStats: async (): Promise<TripReportChunksStats> => {
    const response = await api.get<TripReportChunksStats>('/admin/rag/trip-report-chunks/stats');
    return response.data;
  },

  // ==========================================================================
  // Vertex AI Search API
  // ==========================================================================

  /**
   * Execute a search with basic parameters
   */
  search: async (params: {
    query: string;
    doc_types?: string[];
    categories?: string[];
    tags?: string[];
    page_size?: number;
    enable_reranking?: boolean;
  }): Promise<SearchResult> => {
    const response = await api.post<SearchResult>('/search/', params);
    return response.data;
  },

  /**
   * Execute a search with full SearchConfig
   */
  searchAdvanced: async (config: SearchConfig): Promise<SearchResult> => {
    const response = await api.post<SearchResult>('/search/advanced', config);
    return response.data;
  },

  // ==========================================================================
  // Search Configuration Management (A/B Testing)
  // ==========================================================================

  /**
   * Save a search configuration for A/B testing
   */
  saveSearchConfig: async (
    config: SearchConfig,
    name: string,
    description?: string
  ): Promise<SearchConfigSnapshot> => {
    const queryString = buildQueryString({
      name,
      description,
    });
    const response = await api.post<SearchConfigSnapshot>(
      `/search/configs?${queryString}`,
      config
    );
    return response.data;
  },

  /**
   * List saved search configurations
   */
  listSearchConfigs: async (activeOnly: boolean = true): Promise<SearchConfigSnapshot[]> => {
    const response = await api.get<SearchConfigSnapshot[]>(
      `/search/configs?active_only=${activeOnly}`
    );
    return response.data;
  },

  /**
   * Get a specific search configuration
   */
  getSearchConfig: async (configId: string): Promise<SearchConfigSnapshot> => {
    const response = await api.get<SearchConfigSnapshot>(`/search/configs/${configId}`);
    return response.data;
  },

  /**
   * Deactivate a search configuration (soft delete)
   */
  deactivateSearchConfig: async (configId: string): Promise<{ status: string; id: string }> => {
    const response = await api.delete<{ status: string; id: string }>(`/search/configs/${configId}`);
    return response.data;
  },

  /**
   * Test a saved configuration with a query
   */
  testSearchConfig: async (
    configId: string,
    query: string
  ): Promise<{ id: string; config_id: string; query: string; results_json: Record<string, unknown>; latency_ms: number }> => {
    const queryString = buildQueryString({ query });
    const response = await api.post(`/search/configs/${configId}/test?${queryString}`);
    return response.data;
  },

  // ==========================================================================
  // RAG Test Cases
  // ==========================================================================

  /**
   * Create a new RAG test case
   */
  createTestCase: async (testCase: {
    name: string;
    query: string;
    expected_keywords: string[];
    expected_doc_types: string[];
    category: string;
    min_relevance_score?: number;
  }): Promise<RAGTestCase> => {
    const response = await api.post<RAGTestCase>('/search/test-cases', testCase);
    return response.data;
  },

  /**
   * List RAG test cases
   */
  listTestCases: async (params?: {
    category?: string;
    active_only?: boolean;
  }): Promise<RAGTestCase[]> => {
    const query = buildQueryString({
      category: params?.category,
      active_only: params?.active_only,
    });
    const response = await api.get<RAGTestCase[]>(
      `/search/test-cases${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  // ==========================================================================
  // Test Suite Execution
  // ==========================================================================

  /**
   * Run the full test suite against a configuration
   */
  runTestSuite: async (configId: string): Promise<TestSuiteRun> => {
    const response = await api.post<TestSuiteRun>('/search/test-suite/run', { config_id: configId });
    return response.data;
  },

  /**
   * List test suite runs
   */
  listTestRuns: async (params?: {
    config_id?: string;
    limit?: number;
  }): Promise<TestSuiteRun[]> => {
    const query = buildQueryString({
      config_id: params?.config_id,
      limit: params?.limit,
    });
    const response = await api.get<TestSuiteRun[]>(
      `/search/test-suite/runs${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  /**
   * Get a specific test suite run
   */
  getTestRun: async (runId: string): Promise<TestSuiteRun> => {
    const response = await api.get<TestSuiteRun>(`/search/test-suite/runs/${runId}`);
    return response.data;
  },

  // ==========================================================================
  // FTP Document Sync Jobs
  // ==========================================================================

  /**
   * Get FTP document sync health status
   */
  getDocumentSyncHealth: async (): Promise<DocumentSyncHealth> => {
    const response = await api.get<DocumentSyncHealth>('/admin/documents/health');
    return response.data;
  },

  /**
   * List FTP document sync jobs with pagination
   */
  getDocumentSyncJobs: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<DocumentSyncJobsResponse> => {
    const query = buildQueryString({
      limit: params?.limit,
      offset: params?.offset,
    });
    const response = await api.get<DocumentSyncJobsResponse>(
      `/admin/documents/jobs${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  /**
   * Trigger FTP document sync
   */
  triggerDocumentSync: async (batchLimit: number = 10): Promise<{
    message: string;
    task_id: string;
    triggered_by: string;
  }> => {
    const response = await api.post(`/admin/documents/sync/trigger?batch_limit=${batchLimit}`);
    return response.data;
  },

  // ==========================================================================
  // Red Flag Alerts
  // ==========================================================================

  /**
   * Get count of active red flags by severity
   */
  getRedFlagCount: async (): Promise<{
    critical: number;
    high: number;
    medium: number;
    total: number;
  }> => {
    const response = await api.get('/admin/alerts/red-flags/count');
    return response.data;
  },

  /**
   * List red flags with pagination and filters
   */
  getRedFlags: async (params?: {
    status?: 'active' | 'acknowledged' | 'resolved' | 'ignored';
    include_acknowledged?: boolean;
    include_resolved?: boolean;
    flag_type?: string;
    severity?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    total: number;
    offset: number;
    limit: number;
    flags: RedFlag[];
  }> => {
    const query = buildQueryString({
      status: params?.status,
      include_acknowledged: params?.include_acknowledged ? 'true' : undefined,
      include_resolved: params?.include_resolved ? 'true' : undefined,
      flag_type: params?.flag_type,
      severity: params?.severity,
      limit: params?.limit,
      offset: params?.offset,
    });
    const response = await api.get(`/admin/alerts/red-flags${query ? `?${query}` : ''}`);
    return response.data;
  },

  /**
   * Get a specific red flag
   */
  getRedFlag: async (flagId: string): Promise<RedFlag> => {
    const response = await api.get(`/admin/alerts/red-flags/${flagId}`);
    return response.data;
  },

  /**
   * Acknowledge a red flag
   */
  acknowledgeRedFlag: async (flagId: string): Promise<{
    message: string;
    flag_id: string;
    acknowledged_by: string;
    status: string;
  }> => {
    const response = await api.post(`/admin/alerts/red-flags/${flagId}/acknowledge`);
    return response.data;
  },

  /**
   * Resolve a red flag
   */
  resolveRedFlag: async (flagId: string, notes?: string): Promise<{
    message: string;
    flag_id: string;
    resolved_by: string;
    status: string;
  }> => {
    const params = notes ? `?notes=${encodeURIComponent(notes)}` : '';
    const response = await api.post(`/admin/alerts/red-flags/${flagId}/resolve${params}`);
    return response.data;
  },

  /**
   * Preview what Accept-Sage-as-final would change on a
   * sage_confirmation_mismatch flag's order (read-only).
   */
  previewAcceptSage: async (flagId: string): Promise<AcceptSagePreview> => {
    const response = await api.get(`/admin/alerts/red-flags/${flagId}/accept-sage/preview`);
    return response.data;
  },

  /**
   * Accept live Sage as the final order: rewrite our order to match the
   * Sage SO (audited) and resolve the mismatch flag.
   */
  acceptSage: async (flagId: string): Promise<AcceptSageResult> => {
    const response = await api.post(`/admin/alerts/red-flags/${flagId}/accept-sage`);
    return response.data;
  },

  /**
   * Ignore a red flag
   */
  ignoreRedFlag: async (flagId: string, reason?: string): Promise<{
    message: string;
    flag_id: string;
    ignored_by: string;
    reason?: string;
    status: string;
  }> => {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await api.post(`/admin/alerts/red-flags/${flagId}/ignore${params}`);
    return response.data;
  },

  // ==========================================================================
  // HTML Pages RAG Pipeline (Website Content)
  // ==========================================================================

  /**
   * Get HTML pages pipeline statistics
   */
  getHtmlPagesStats: async (): Promise<HtmlPagesStats> => {
    const response = await api.get<HtmlPagesStats>('/admin/rag/html-pages/stats');
    return response.data;
  },

  /**
   * List HTML pages with pagination and filters
   */
  getHtmlPages: async (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    page_type?: string;
    search?: string;
  }): Promise<HtmlPagesResponse> => {
    const query = buildQueryString({
      limit: params?.limit,
      offset: params?.offset,
      status: params?.status,
      page_type: params?.page_type,
      search: params?.search,
    });
    const response = await api.get<HtmlPagesResponse>(
      `/admin/rag/html-pages${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  /**
   * Get detailed HTML page info with chunks
   */
  getHtmlPage: async (pageId: string): Promise<HtmlPageDetail> => {
    const response = await api.get<HtmlPageDetail>(`/admin/rag/html-pages/${pageId}`);
    return response.data;
  },

  /**
   * List HTML page chunks with pagination and filters
   */
  getHtmlChunks: async (params?: {
    limit?: number;
    offset?: number;
    html_page_id?: string;
    is_useless?: boolean;
    has_summary?: boolean;
    search?: string;
  }): Promise<HtmlChunksResponse> => {
    const query = buildQueryString({
      limit: params?.limit,
      offset: params?.offset,
      html_page_id: params?.html_page_id,
      is_useless: params?.is_useless,
      has_summary: params?.has_summary,
      search: params?.search,
    });
    const response = await api.get<HtmlChunksResponse>(
      `/admin/rag/html-chunks${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  // ==========================================================================
  // Ingestion Pipeline Admin
  // ==========================================================================

  /**
   * Get ingestion pipeline statistics
   */
  getIngestionStats: async (): Promise<IngestionStats> => {
    const response = await api.get<IngestionStats>('/admin/ingestion/stats');
    return response.data;
  },

  /**
   * List ingestion records with filters and pagination
   */
  getIngestionRecords: async (params?: {
    limit?: number;
    offset?: number;
    state?: string;
    source?: string;
    category_id?: string;
    search?: string;
  }): Promise<IngestionRecordsResponse> => {
    const query = buildQueryString({
      limit: params?.limit,
      offset: params?.offset,
      state: params?.state,
      source: params?.source,
      category_id: params?.category_id,
      search: params?.search,
    });
    const response = await api.get<IngestionRecordsResponse>(
      `/admin/ingestion/records${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  /**
   * Get detailed ingestion record with audit log
   */
  getIngestionRecord: async (recordId: string): Promise<IngestionRecordDetail> => {
    const response = await api.get<IngestionRecordDetail>(
      `/admin/ingestion/records/${recordId}`
    );
    return response.data;
  },

  /**
   * Retry a failed ingestion record
   */
  retryIngestionRecord: async (recordId: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(
      `/admin/ingestion/records/${recordId}/retry`
    );
    return response.data;
  },

  /**
   * Admin classify an ingestion record
   */
  classifyIngestionRecord: async (
    recordId: string,
    categoryId: string
  ): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(
      `/admin/ingestion/records/${recordId}/classify`,
      { category_id: categoryId }
    );
    return response.data;
  },

  /**
   * Reject an ingestion record
   */
  rejectIngestionRecord: async (
    recordId: string,
    reason: string
  ): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(
      `/admin/ingestion/records/${recordId}/reject`,
      { reason }
    );
    return response.data;
  },

  /**
   * Get ingestion categories
   */
  getIngestionCategories: async (): Promise<IngestionCategory[]> => {
    const response = await api.get<IngestionCategory[]>(
      '/admin/ingestion/categories'
    );
    return response.data;
  },

  /**
   * Approve a new ingestion category
   */
  approveIngestionCategory: async (
    categoryId: string
  ): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(
      `/admin/ingestion/categories/${categoryId}/approve`
    );
    return response.data;
  },

  /**
   * Toggle sandbox mode on/off (staging only)
   */
  toggleSandboxMode: async (
    enabled: boolean
  ): Promise<SandboxStatusResponse> => {
    const response = await api.post<SandboxStatusResponse>(
      '/admin/sandbox',
      { enabled }
    );
    return response.data;
  },

  /**
   * Get current sandbox mode status
   */
  getSandboxStatus: async (): Promise<SandboxStatusResponse> => {
    const response = await api.get<SandboxStatusResponse>('/admin/sandbox');
    return response.data;
  },

  /**
   * Set the sandbox redirect email (staging only)
   */
  setSandboxRedirect: async (
    redirectEmail: string
  ): Promise<SandboxStatusResponse> => {
    const response = await api.post<SandboxStatusResponse>(
      '/admin/sandbox/redirect',
      { redirect_email: redirectEmail }
    );
    return response.data;
  },
};

// Sandbox status response from admin API
export interface SandboxStatusResponse {
  sandbox_mode: boolean;
  environment: string;
  is_override: boolean;
  sandbox_redirect_email: string;
}

// Red Flag type definition
export interface RedFlag {
  id: string;
  flag_type: string;
  severity: 'critical' | 'high' | 'medium';
  status: 'active' | 'acknowledged' | 'resolved' | 'ignored';
  title: string;
  message: string;
  details: Record<string, unknown> | null;
  related_document_id: string | null;
  related_ftp_path: string | null;
  created_at: string;
  updated_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
}

export interface AcceptSageFieldChange {
  field: string;
  old: string | null;
  new: string | null;
}

export interface AcceptSageLineUpdate {
  line_item_id: string;
  position: number;
  item_code: string;
  changes: AcceptSageFieldChange[];
}

export interface AcceptSageLineAdd {
  item_code: string;
  item_description: string;
  quantity: string | number;
  unit_price: string | number;
  sage_line_key: string | null;
}

export interface AcceptSageLineRemove {
  line_item_id: string;
  position: number;
  item_code: string;
  quantity: string | number | null;
  unit_price: string | number | null;
}

/** What Accept-Sage-as-final would change (GET .../accept-sage/preview). */
export interface AcceptSagePreview {
  flag_id: string;
  order_id: string;
  sales_order_no: string;
  source: 'live' | 'mirror';
  captured_at: string;
  line_updates: AcceptSageLineUpdate[];
  line_adds: AcceptSageLineAdd[];
  line_removes: AcceptSageLineRemove[];
  header_changes: AcceptSageFieldChange[];
  skipped_sage_rows: string[];
  subtotal_before: string | number;
  subtotal_after: string | number;
  blockers: string[];
  is_noop: boolean;
  can_apply: boolean;
  summary: string;
}

/** What the write-back did (POST .../accept-sage). */
export interface AcceptSageResult {
  preview: AcceptSagePreview;
  accepted_at: string;
  accepted_by: string;
  lines_updated: number;
  lines_added: number;
  lines_removed: number;
  header_fields_changed: number;
  resolved_flag_count: number;
  subtotal: string | number;
}

// LLM Payload type definition - what gets passed to the LLM (for database chunks)
export interface ChunkLLMPayload {
  chunk_id: string;
  document_filename: string;
  raw_fields: {
    content: string;
    heading_context: string;
    source_document: string;
    doc_type: string;
    page_number: number | null;
    chunk_index: number;
    content_type: string;
  };
  formatted_context: string;
  metadata: {
    has_summary: boolean;
    is_useless: boolean;
    token_count: number;
    summary_tokens: number;
    raw_text_length: number;
    content_length: number;
  };
}

// Search result item type - matches SearchResultItem from backend
export interface SearchResultItem {
  id: string;
  content: string;
  heading_context: string;
  doc_type: string;
  category: string;
  source_document: string;
  page_number: number | null;
  chunk_index: number | null;
  relevance_score: number | null;
  snippet: string | null;
  raw_data: Record<string, unknown>;
}

// LLM Payload type definition for search results
export interface SearchResultLLMPayload {
  vertex_doc_id: string;
  source_document: string;
  source_type: string;
  raw_fields: {
    content: string;
    heading_context: string;
    source_document: string;
    source_url: string;
    source_type: string;
    doc_type: string;
    page_number: number | null;
    chunk_index: number | null;
    content_type: string;
    category: string;
    relevance_score: number | null;
  };
  formatted_context: string;
  mcp_format: {
    content: string;
    heading: string;
    doc_type: string;
    category: string;
    source: string;
    source_type: string;
    page: number | null;
    relevance: number | null;
  };
  metadata: {
    chunk_index: number | null;
    page_number: number | null;
    relevance_score: number | null;
    raw_text_length: number;
    content_length: number;
    doc_type: string;
    category: string;
  };
}
